import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import tuttoGymLogo from '../../images/tuttogym.jpeg';
import { login } from '../../services/api';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    console.log('Login attempt with email:', email);

    try {
      // Use the authentication API to login
      const response = await login(email, password);
      console.log('Login response received:', response);
      
      // If login is successful, redirect to dashboard based on user type
      if (response.success) {
        const userRole = response.user.role || 'customer';
        console.log('Login successful for user type:', userRole);
        
        // Adding a small delay to show the animation before navigation
        setTimeout(() => {
          // Force a clean navigation that triggers layout changes
          let targetPath;
          switch(userRole) {
            case 'customer':
              console.log('Redirecting to customer dashboard');
              targetPath = '/panel/customer-dashboard';
              break;
            case 'company':
              console.log('Redirecting to company dashboard');
              targetPath = '/panel/company-dashboard';
              break;
            case 'partner':
              console.log('Redirecting to partner dashboard');
              targetPath = '/panel/partner-dashboard';
              break;
            default:
              console.log('Redirecting to default dashboard');
              targetPath = '/panel/dashboard';
              break;
          }
          
          // Use replace instead of navigate to ensure a clean state transition
          // This forces route re-evaluation and proper layout changes
          window.location.replace(targetPath);
          
          // We don't reset loading state here as we're navigating away
        }, 1000); // 1 second delay to show the animation
      } else {
        setError(response.message || 'Error al iniciar sesión. Por favor, inténtalo de nuevo.');
        console.error('Login failed:', response.message);
        setLoading(false);
      }
    } catch (err) {
      setError('Credenciales incorrectas o error de conexión. Por favor, inténtalo de nuevo.');
      console.error('Error de inicio de sesión:', err);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      {/* Loading Animation Backdrop */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">Iniciando sesión...</Typography>
      </Backdrop>

      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={tuttoGymLogo}
            alt="TuttoGym Logo"
            sx={{
              height: 60,
              mb: 2,
            }}
          />
          
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Panel de Administración
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                my: 3, 
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Si eres partner te redirigiremos al panel de partners, si eres una empresa al panel de empresas y si eres cliente al perfil del cliente
              </Typography>
            </Paper>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </Button>
          </Box>
          

        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
